import { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Send, MapPin, Phone, Mail, Clock } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const subject = `Bericht van ${formData.name}`;
      const body = `Naam: ${formData.name}\nEmail: ${formData.email}\nTelefoon: ${formData.phone || '-'}\n\n${formData.message}`;
      window.location.href = `mailto:info@barbaarmiddelkerke.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      toast.success(t.contactPage.success);
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(t.contactPage.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#0A0A0A] pt-24" data-testid="contact-page">
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-body text-sm tracking-[0.2em] text-copper mb-4 uppercase"
          >
            {t.contactPage.subtitle}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-heading text-5xl md:text-7xl font-bold text-[#EDEDED] mb-6"
          >
            {t.contactPage.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="font-body text-[#A1A1A1] text-lg max-w-2xl mx-auto"
          >
            {t.contactPage.description}
          </motion.p>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="glass-card p-8 md:p-12">
                <h2 className="font-heading text-2xl font-semibold text-[#EDEDED] mb-8">
                  {t.contactPage.formTitle}
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="font-body text-sm text-[#A1A1A1]">
                      {t.contactPage.name} *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="bg-transparent border-0 border-b border-[#2A2A2A] rounded-none px-0 py-3 text-[#EDEDED] focus:border-copper focus:ring-0 font-body"
                      placeholder={t.contactPage.namePlaceholder}
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="font-body text-sm text-[#A1A1A1]">
                      {t.contactPage.email} *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="bg-transparent border-0 border-b border-[#2A2A2A] rounded-none px-0 py-3 text-[#EDEDED] focus:border-copper focus:ring-0 font-body"
                      placeholder={t.contactPage.emailPlaceholder}
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="font-body text-sm text-[#A1A1A1]">
                      {t.contactPage.phone}
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="bg-transparent border-0 border-b border-[#2A2A2A] rounded-none px-0 py-3 text-[#EDEDED] focus:border-copper focus:ring-0 font-body"
                      placeholder={t.contactPage.phonePlaceholder}
                      data-testid="contact-phone-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="font-body text-sm text-[#A1A1A1]">
                      {t.contactPage.message} *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      className="bg-transparent border-0 border-b border-[#2A2A2A] rounded-none px-0 py-3 text-[#EDEDED] focus:border-copper focus:ring-0 font-body resize-none"
                      placeholder={t.contactPage.messagePlaceholder}
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-copper hover:bg-copper-dark text-white font-body text-sm tracking-wider py-6 rounded-sm btn-copper mt-8"
                    data-testid="contact-submit-btn"
                  >
                    {loading ? t.contactPage.sending : (
                      <>
                        {t.contactPage.send} <Send className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div className="glass-card p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-copper/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-copper" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-[#EDEDED] mb-2">{t.contactPage.address}</h3>
                    <p className="font-body text-[#A1A1A1]">
                      Parijsstraat 26<br />
                      8430 Middelkerke<br />
                      Belgium
                    </p>
                  </div>
                </div>
              </div>

              <div className="glass-card p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-copper/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-copper" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-[#EDEDED] mb-2">{t.contactPage.phone}</h3>
                    <a href="tel:+32000000000" className="font-body text-[#A1A1A1] hover:text-copper transition-colors">
                      +32 (0) XX XX XX XX
                    </a>
                  </div>
                </div>
              </div>

              <div className="glass-card p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-copper/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-copper" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-[#EDEDED] mb-2">{t.contactPage.email}</h3>
                    <a href="mailto:info@barbaarmiddelkerke.com" className="font-body text-[#A1A1A1] hover:text-copper transition-colors">
                      info@barbaarmiddelkerke.com
                    </a>
                  </div>
                </div>
              </div>

              <div className="glass-card p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-copper/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-copper" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-[#EDEDED] mb-2">{t.contactPage.openingHours}</h3>
                    <div className="font-body text-[#A1A1A1] space-y-1 text-sm">
                      <p><span className="text-[#EDEDED]">{t.days.monday}:</span> 11u30 - 22u30</p>
                      <p><span className="text-[#EDEDED]">{t.days.tuesday}:</span> 11u30 - 22u30</p>
                      <p><span className="text-[#666]">{t.days.wednesday}:</span> <span className="text-[#666]">{t.contactPage.closed}</span></p>
                      <p><span className="text-[#666]">{t.days.thursday}:</span> <span className="text-[#666]">{t.contactPage.closed}</span></p>
                      <p><span className="text-[#EDEDED]">{t.days.friday}:</span> 11u30 - 00u00</p>
                      <p><span className="text-[#EDEDED]">{t.days.saturday}:</span> 11u30 - 00u00</p>
                      <p><span className="text-[#EDEDED]">{t.days.sunday}:</span> 11u30 - 22u30</p>
                      <p className="text-copper mt-1 text-xs">{t.contactPage.kitchenNote}</p>
                      <p className="text-copper mt-3 text-xs font-semibold">{t.contactPage.summerTitle}</p>
                      <p><span className="text-[#666]">{t.days.monday}:</span> <span className="text-[#666]">{t.contactPage.closed}</span></p>
                      <p><span className="text-[#EDEDED]">{t.days.tuesday}:</span> 12u00 - 00u00</p>
                      <p><span className="text-[#EDEDED]">{t.days.wednesday}:</span> 12u00 - 00u00</p>
                      <p><span className="text-[#EDEDED]">{t.days.thursday}:</span> 12u00 - 00u00</p>
                      <p><span className="text-[#EDEDED]">{t.days.friday}:</span> 12u00 - 00u00</p>
                      <p><span className="text-[#EDEDED]">{t.days.saturday}:</span> 12u00 - 00u00</p>
                      <p><span className="text-[#EDEDED]">{t.days.sunday}:</span> 12u00 - 00u00</p>
                      <p className="text-copper mt-1 text-xs">{t.contactPage.summerKitchenNote}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <a
                  href="https://www.instagram.com/barbaar_middelkerke/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 glass-card p-6 text-center hover:border-copper transition-colors"
                  data-testid="contact-instagram"
                >
                  <svg className="w-6 h-6 text-copper mx-auto mb-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                  <span className="font-body text-sm text-[#A1A1A1]">Instagram</span>
                </a>
                <a
                  href="https://www.facebook.com/barbaar.middelkerke/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 glass-card p-6 text-center hover:border-copper transition-colors"
                  data-testid="contact-facebook"
                >
                  <svg className="w-6 h-6 text-copper mx-auto mb-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  <span className="font-body text-sm text-[#A1A1A1]">Facebook</span>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
